import { apiService } from "../../../../components/api/apiService";

const DEVELOPER_ACCESS_BASE = "/api/DeveloperAccess";

export interface UserListDto {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roleName: string;
}

export interface RoleOption {
  roleId: string;
  roleName: string;
}

export const ENVIRONMENTS = ["production", "Development", "staging", "other"] as const;
export type EnvironmentOption = (typeof ENVIRONMENTS)[number];

export interface IssueTokenPayload {
  developerUserId: string;
  roleName?: string;
  environment?: string;
  selectedMCPResourceIds: string[];
  expiry: string;
  label?: string;
  notes?: string;
  rateLimitPerMinute?: number;
}

export interface IssueTokenResponse {
  id: string;
  token: string;
  developerUserId: string;
  developerName: string;
  developerEmail?: string;
  roleName?: string;
  selectedMCPResourceIds: string[];
  expiry: string;
  expiresAt?: string;
  label?: string;
  rateLimitPerMinute?: number;
  issuedAt: string;
  isActive: boolean;
}

export interface IssuedTokenRow {
  id: string;
  dev: string;
  role: string;
  env: string;
  token: string;
  issued: string;
  isActive: boolean;
  init: string;
  grad: string;
}

const GRADIENTS = [
  "from-violet-600 to-indigo-500",
  "from-orange-600 to-yellow-500",
  "from-teal-600 to-blue-500",
  "from-purple-600 to-pink-500",
  "from-orange-500 to-red-500",
  "from-teal-500 to-emerald-400",
  "from-zinc-600 to-zinc-400",
];

function initFromName(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

export const developerAccessService = {
  async getUsers(): Promise<UserListDto[]> {
    const data = await apiService.get("/api/auth/users", {}, "backend");
    const list = Array.isArray(data) ? data : [];
    return list.map((u: { id: string; email: string; firstName?: string; lastName?: string; roleName?: string }) => ({
      id: u.id,
      email: u.email ?? "",
      firstName: u.firstName ?? "",
      lastName: u.lastName ?? "",
      roleName: u.roleName ?? "",
    }));
  },

  async getRoles(): Promise<RoleOption[]> {
    const data = await apiService.get("/api/auth/roles/users", {}, "backend");
    const list = Array.isArray(data) ? data : [];
    return list.map((r: { roleId?: string; roleName?: string }) => ({
      roleId: r.roleId ?? "",
      roleName: r.roleName ?? "",
    })).filter((r: RoleOption) => r.roleName);
  },

  async issueToken(payload: IssueTokenPayload): Promise<IssueTokenResponse> {
    const res = await apiService.post(
      DEVELOPER_ACCESS_BASE,
      {
        developerUserId: payload.developerUserId,
        roleName: payload.roleName ?? null,
        environment: payload.environment ?? null,
        selectedMCPResourceIds: payload.selectedMCPResourceIds,
        expiry: payload.expiry,
        label: payload.label ?? null,
        notes: payload.notes ?? null,
        rateLimitPerMinute: payload.rateLimitPerMinute ?? null,
      },
      "backend"
    );
    return res;
  },

  async getIssuedTokens(): Promise<IssuedTokenRow[]> {
    const data = await apiService.get(DEVELOPER_ACCESS_BASE, {}, "backend");
    const list = data?.tokens ?? [];
    return list.map((t: { id: string; developerName: string; roleName?: string; environment?: string; tokenMasked: string; issuedAt: string; isActive: boolean }, i: number) => {
      const name = t.developerName ?? "";
      return {
        id: t.id,
        dev: name,
        role: t.roleName ?? "",
        env: t.environment ?? "production",
        token: t.tokenMasked ?? "mcp-tok-…",
        issued: typeof t.issuedAt === "string" ? t.issuedAt.slice(0, 10) : new Date(t.issuedAt).toISOString().slice(0, 10),
        isActive: t.isActive ?? true,
        init: initFromName(name),
        grad: GRADIENTS[i % GRADIENTS.length],
      };
    });
  },

  async revokeToken(id: string): Promise<IssuedTokenRow | null> {
    const res = await apiService.put(`${DEVELOPER_ACCESS_BASE}/${id}/revoke`, {}, "backend");
    if (!res) return null;
    return mapTokenToRow(res);
  },

  async reactivateToken(id: string): Promise<IssuedTokenRow | null> {
    const res = await apiService.put(`${DEVELOPER_ACCESS_BASE}/${id}/reactivate`, {}, "backend");
    if (!res) return null;
    return mapTokenToRow(res);
  },

  async deleteToken(id: string): Promise<boolean> {
    await apiService.delete(`${DEVELOPER_ACCESS_BASE}/${id}`, "backend");
    return true;
  },
};

function mapTokenToRow(res: {
  id: string;
  developerName?: string;
  roleName?: string;
  environment?: string;
  tokenMasked?: string;
  issuedAt: string | Date;
  isActive?: boolean;
}): IssuedTokenRow {
  return {
    id: res.id,
    dev: res.developerName ?? "",
    role: res.roleName ?? "",
    env: res.environment ?? "production",
    token: res.tokenMasked ?? "mcp-tok-…",
    issued: typeof res.issuedAt === "string" ? res.issuedAt.slice(0, 10) : new Date(res.issuedAt).toISOString().slice(0, 10),
    isActive: res.isActive ?? false,
    init: initFromName(res.developerName ?? ""),
    grad: GRADIENTS[0],
  };
}
